import { IColor } from '../interfaces';
import { getRGBAString } from './color-provider';
import { IconBackgroundType } from '../enums/IconBackgroundType';
import uniqueId from 'lodash-es/uniqueId';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/**
 * init SVG in browser environment ---> return DOM svg element
 */
export const parseSVG = (svg: string): SVGSVGElement => {
    const $box = document.createElement('div');
    $box.innerHTML = svg;
    return $box.firstElementChild as SVGSVGElement;
};

/**
 * draw a rectangle
 */
export const drawRect = (
    x: number, y: number, width: string, height: string, bgColor?: string,
    strokeColor?: string, strokeWidth?: number, rx?: number, ry?: number
) => {

    const $rect = document.createElementNS(SVG_NAMESPACE, 'rect');

    $rect.setAttribute('x', x.toString());
    $rect.setAttribute('y', y.toString());

    $rect.setAttribute('width', width);
    $rect.setAttribute('height', height);

    if(strokeWidth){
        $rect.setAttribute('stroke-width', strokeWidth.toFixed(2));
    }
    else{
        $rect.setAttribute('stroke', 'none')
        $rect.setAttribute('stroke-width', '0');
    }

    if(strokeColor){
        $rect.setAttribute('stroke', strokeColor);
    }

    if(bgColor) {
        $rect.setAttribute('fill', bgColor);
    }

    if(rx){
        $rect.setAttribute('rx', rx.toString());
    }

    if(ry){
        $rect.setAttribute('ry', ry.toString());
    }

    return $rect;
};


/**
 * draw a circle
 */
export const drawCircle = (
    centerX: string, centerY: string, radius: string, bgColor: string,
    strokeWidth?: number, strokeColor?: string,
): SVGCircleElement => {

    const $circle = document.createElementNS(SVG_NAMESPACE, 'circle');

    $circle.setAttribute('cx', centerX);
    $circle.setAttribute('cy', centerY);
    $circle.setAttribute('r', radius.toString());

    if(strokeWidth){
        $circle.setAttribute('stroke-width', strokeWidth.toFixed(2));
    }
    else{
        $circle.setAttribute('stroke', 'none')
        $circle.setAttribute('stroke-width', '0');
    }


    if(strokeColor){
        $circle.setAttribute('stroke', strokeColor);
    }

    if(bgColor) {
        $circle.setAttribute('fill', bgColor);
    }

    return $circle;
};

export const addFilter = (
    $svg: SVGSVGElement,
    $container: SVGGElement,
    filterId: string,
    kernelMatrix: number[],
) => {

    let $defs = $svg.querySelector('defs');
    if(!$defs){
        $defs = document.createElementNS(SVG_NAMESPACE, 'defs');
        $svg.prepend($defs);
    }

    const $filter = document.createElementNS(SVG_NAMESPACE, 'filter');
    $filter.setAttribute('id', filterId);
    $filter.setAttribute('x', '0');
    $filter.setAttribute('y', '0');
    $filter.setAttribute('width', '100%');
    $filter.setAttribute('height', '100%');
    $defs.append($filter);

    const $feConvolveMatrix = document.createElementNS(SVG_NAMESPACE, 'feConvolveMatrix');
    $feConvolveMatrix.setAttribute('kernelMatrix', kernelMatrix.join(' '));
    $filter.append($feConvolveMatrix);

    // style="filter:url(#convolveMatrix2);
    //$container.style.filter = `url(#${ filterId });`;

    return `url(#${ filterId })`;
};

export const applySVGProperties = (
    $svg: SVGSVGElement,
    size: number,

    strokeWidth: number,
    strokeColor: IColor,
    hasStroke: boolean,

    fillColor: IColor,
    hasFill: boolean,

    backgroundType: IconBackgroundType,
    backgroundColor: IColor,

    rotation: number,
    scale: number,

    sharpness: number,
    smoothness: number
) => {

    $svg.setAttribute('width', `${ size }px`);
    $svg.setAttribute('height', `${ size }px`);

    if(hasStroke){
        $svg.setAttribute('stroke', getRGBAString(strokeColor));
        $svg.setAttribute('stroke-width', strokeWidth.toFixed(2));
    }
    else{
        $svg.setAttribute('stroke-width', '0');
    }

    $svg.setAttribute('fill', hasFill ? getRGBAString(fillColor) : 'none');

    // put all inner content into the <g></g> element, so we could apply transformations on them
    const $g = document.createElementNS(SVG_NAMESPACE, 'g');
    while ($svg.firstElementChild) {
        $g.append($svg.firstElementChild);
    }

    $svg.prepend($g);

    // the default background is 'Transparent'
    switch (backgroundType){
        case IconBackgroundType.Rectangle: {
            const $bg = drawRect(0, 0, '100%', '100%', getRGBAString(backgroundColor));
            $svg.prepend($bg);
            break;
        }

        case IconBackgroundType.RoundedRectangle: {
            const $bg = drawRect(0, 0, '100%', '100%', getRGBAString(backgroundColor), '', 0, 5, 5);
            $svg.prepend($bg);

            /*
            // in case of circle, all content should be a bit smaller
            // if the center is (10, 20) and you are scaling by 3 then translate by (1 - 3)*10, (1 - 3)*20 = (-20, -40)
            // 12 is half of viewport size
            const scale = 0.8;
            const translate = (1 - scale) * (12);
            $g.setAttribute('transform', `translate(${ translate.toFixed(2) }, ${ translate.toFixed(2) }) scale(${ scale })`);*/
            break;
        }

        case IconBackgroundType.Circle: {
            const $bg = drawCircle('50%', '50%', '50%', getRGBAString(backgroundColor));
            $svg.prepend($bg);

            /*
            // in case of circle, all content should be a bit smaller
            // if the center is (10, 20) and you are scaling by 3 then translate by (1 - 3)*10, (1 - 3)*20 = (-20, -40)
            // 12 is half of viewport size
            const scale = 0.8;
            const translate = (1 - scale) * (12);
            $g.setAttribute('transform', `translate(${ translate.toFixed(2) }, ${ translate.toFixed(2) }) scale(${ scale })`);*/
            break;
        }
    }

    if(scale){
        const translate = (1 - scale) * (12);
        $g.setAttribute('transform', `translate(${ translate.toFixed(2) }, ${ translate.toFixed(2) }) scale(${ scale })`);
    }

    if(rotation){
        const $rotateG = document.createElementNS(SVG_NAMESPACE, 'g');
        while ($g.firstElementChild) {
            $rotateG.append($g.firstElementChild);
        }
        $rotateG.setAttribute('transform', `rotate(${ rotation } 12 12)`); // 12 is viewport width / 2, viewport height / 2 !!!
        $g.append($rotateG);
    }

    const filters: string[] = [];

    // apply filters
    if(sharpness){
        const val = sharpness;
        filters.push(addFilter(
            $svg,
            $g,
            uniqueId('sharpness-filter'),
            [
                0,       -1 * val,    0,
                -1 * val,  5 * val,   -1 * val,
                0,       -1 * val,    0,
            ],
        ));
    }

    if(smoothness){
        const val = smoothness;
        filters.push(addFilter(
            $svg,
            $g,
            uniqueId('smoothness-filter'),
            [
                -val, -val,         -val,
                -val, -8 * val + 1, -val,
                -val, -val,         -val,
            ],
        ));
    }

    if(filters.length > 0){
        $g.style.filter = filters.join(' ');
    }

    // return $svg.outerHTML;
    return (new XMLSerializer()).serializeToString($svg);
};
